import React, { useState, useEffect, useRef } from "react";
import clsx from "clsx";
import Swal from 'sweetalert2';
import { ProductSearch } from '../generatedRequest/product';
import { syncAttribute } from '../generatedRequest/master';
import MasterCustomTooltip from "../utils/CustomStyleComponent/MasterModelTooltip";

interface MasterModelSelectorProps {
    onMasterModelChange: (masterModel: any) => void;
    onKeywordChange?: (keyword: string) => void;
    onFormDataChange?: (data: { keyword: string, masterModel: any, selectedAttributesData?: any, selectedAttributes?: { [key: number]: boolean } }) => void;
    formik: any;
    isEdit?: boolean;
    masterData?: any;
    onSyncEdit?: (syncData: any) => void;
    data: any;
    showSyncButton?: boolean; // New prop to control sync button visibility
}

const MasterModelSelector: React.FC<MasterModelSelectorProps> = ({
    onMasterModelChange,
    formik,
    onKeywordChange,
    onFormDataChange,
    masterData,
    isEdit,
    onSyncEdit,
    data,
    showSyncButton = false // Default to false, will be true only for EditMasterForm

}) => {
    const [productName, setProductName] = useState("");
    const [productId, setProductId] = useState<number | null>(null);
    const [productSuggestions, setProductSuggestions] = useState<any[]>([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMoreResults, setHasMoreResults] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [lastSearchQuery, setLastSearchQuery] = useState("");
    const [keyword, setKeyword] = useState("");
    const [selectedAttributes, setSelectedAttributes] = useState<{ [key: number]: boolean }>({});
    const [isSyncing, setIsSyncing] = useState(false);
    const [masterModel, setMasterModel] = useState<{
        id: number | null;
        displayName: string | null;
        productName: string | null;
        modelNumber: string | null;
        attributes: [] | null;
    }>({
        id: null,
        displayName: null,
        productName: null,
        modelNumber: null,
        attributes: null,
    });

    const searchContainerRef = useRef<HTMLDivElement | null>(null);


    // Check if master model is deleted
    const isMasterModelDeleted = isEdit && masterData && masterData.masterModel && masterData.masterModel.id === null;

    useEffect(() => {
        if (isEdit && masterData) {
            if (masterData.keyword) {
                setKeyword(masterData.keyword);
            }

            if (masterData.masterModel) {
                let masterModelData;

                if (Array.isArray(masterData.masterModel) && masterData.masterModel.length > 0) {
                    masterModelData = masterData.masterModel[0];
                } else if (masterData.masterModel && typeof masterData.masterModel === 'object') {
                    masterModelData = masterData.masterModel;
                }

                if (masterModelData) {
                    const newMasterModel = {
                        id: masterModelData.id || masterModelData._id || null,
                        displayName: masterModelData.displayName || null,
                        productName: masterModelData.productName || null,
                        modelNumber: masterModelData.modelNumber || null,
                        attributes: masterModelData.attributes || null,
                    };

                    setMasterModel(newMasterModel);

                    // Initialize all attributes as unselected (normal state)
                    if (masterModelData.attributes && Array.isArray(masterModelData.attributes)) {
                        const initialSelected: { [key: number]: boolean } = {};

                        // Set all attributes as unselected (false) regardless of syncAttributeselected
                        masterModelData.attributes.forEach((_: any, index: number) => {
                            initialSelected[index] = false;
                        });

                        setSelectedAttributes(initialSelected);
                    }

                    if (masterModelData.displayName || masterModelData.productName) {
                        setProductName(masterModelData.displayName || masterModelData.productName);
                    }

                    if (masterModelData.id || masterModelData._id) {
                        setProductId(masterModelData.id || masterModelData._id);
                    }
                }
            }
        }
    }, [masterData, isEdit]);

    useEffect(() => {
        const handleClickOutside = (event: any) => {
            if (
                searchContainerRef.current &&
                searchContainerRef.current.contains &&
                !searchContainerRef.current.contains(event.target)
            ) {
                setShowSuggestions(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const searchProductsFromAPI = async (search: string, page: number = 1, append: boolean = false) => {
        if (!search || search.trim() === '') {
            setProductSuggestions([]);
            setShowSuggestions(false);
            return;
        }

        try {
            if (page === 1) {
                setIsLoading(true);
            } else {
                setLoadingMore(true);
            }

            const trimmedQuery = search.trim();
            setLastSearchQuery(trimmedQuery);

            const response = await ProductSearch(`search=${trimmedQuery}&page=${page}`);

            if (response && response.status === 200) {
                let newResults: any[] = [];
                let hasMore = false;

                if (response.data.data.data) {
                    if (Array.isArray(response.data.data.data)) {
                        newResults = response.data.data.data;
                        hasMore = newResults.length === 10;
                    } else if (response.data.data.data.results && Array.isArray(response.data.data.data.results)) {
                        newResults = response.data.data.data.results;
                        hasMore = response.data.data.data.hasMore ||
                            (response.data.data.data.total_pages && page < response.data.data.data.total_pages) ||
                            newResults.length === 10;
                    } else if (response.data.data.data && Array.isArray(response.data.data)) {
                        newResults = response.data.data.data;
                        hasMore = newResults.length === 10;
                    }

                    setHasMoreResults(hasMore);

                    if (append) {
                        setProductSuggestions(prev => [...prev, ...newResults]);
                    } else {
                        setProductSuggestions(newResults);
                    }

                    setShowSuggestions(newResults.length > 0);
                } else {
                    if (!append) {
                        setProductSuggestions([]);
                        setShowSuggestions(false);
                    }
                    setHasMoreResults(false);
                }
            } else {
                console.error("API request failed:", response || "Unknown error");
                if (!append) {
                    setProductSuggestions([]);
                    setShowSuggestions(false);
                }
                setHasMoreResults(false);
            }
        } catch (error) {
            console.error("Error searching products:", error);
            if (!append) {
                setProductSuggestions([]);
                setShowSuggestions(false);
            }
            setHasMoreResults(false);
        } finally {
            setIsLoading(false);
            setLoadingMore(false);
        }
    };

    const handleSearch = () => {
        setCurrentPage(1);
        searchProductsFromAPI(productName);
    };

    const loadMoreResults = () => {
        const nextPage = currentPage + 1;
        setCurrentPage(nextPage);
        searchProductsFromAPI(lastSearchQuery, nextPage, true);
    };

    const handleProductNameChange = (e: any) => {
        const value = e.target.value;
        setProductName(value);
        setProductId(null);
    };

    const handleKeywordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setKeyword(value);
        if (onKeywordChange) {
            onKeywordChange(value);
        }
        if (onFormDataChange) {
            onFormDataChange({ keyword: value, masterModel });
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            setCurrentPage(1);
            searchProductsFromAPI(productName);
        }
    };

    const handleSelectProduct = (selectedProduct: any) => {
        const productName = selectedProduct.displayName || selectedProduct.productName;
        const productId = selectedProduct._id || null;
        setProductName(productName);
        setProductId(productId);
        setShowSuggestions(false);

        const newMasterModel = {
            id: selectedProduct._id || null,
            displayName: selectedProduct.displayName || null,
            productName: selectedProduct.productName || null,
            modelNumber: selectedProduct.modelNumber || null,
            attributes: selectedProduct.attributes || null,
        };

        setMasterModel(newMasterModel);

        // Reset selected attributes when new product is selected
        if (selectedProduct.attributes && Array.isArray(selectedProduct.attributes)) {
            const initialSelected: { [key: number]: boolean } = {};
            selectedProduct.attributes.forEach((_: any, index: number) => {
                initialSelected[index] = false;
            });
            setSelectedAttributes(initialSelected);
        }

        if (onMasterModelChange) {
            onMasterModelChange(newMasterModel);
        }

        if (onFormDataChange) {
            onFormDataChange({ keyword, masterModel: newMasterModel });
        }
    };

    const getSelectedAttributesArray = (selectedAttrs: { [key: number]: boolean }) => {
        if (masterModel.attributes && Array.isArray(masterModel.attributes) && masterModel.id) {
            const selectedAttributesOnly = masterModel.attributes
                .map((attr: any, index) => {
                    if (selectedAttrs[index]) {
                        return {
                            attributeName: attr.attributeName || `Attribute ${index + 1}`,
                            value: attr.value || "N/A",
                            unit: attr.unit || {},
                        };
                    }
                    return null;
                })
                .filter(attr => attr !== null);

            if (selectedAttributesOnly.length > 0) {
                return {
                    masterModelId: masterModel.id,
                    productName: masterModel.displayName || masterModel.productName,
                    selectedAttributes: selectedAttributesOnly
                };
            }
        }
        return null;
    };

    const handleSyncAttributes = async () => {
        const selectedAttributesData = getSelectedAttributesArray(selectedAttributes);

        if (!selectedAttributesData || selectedAttributesData.selectedAttributes.length === 0) {
            Swal.fire({
                icon: 'warning',
                title: 'No Attribute Selected',
                text: 'Please select at least one attribute.',
                confirmButtonText: 'OK'
            });
            return;
        }

        const selectedCount = selectedAttributesData.selectedAttributes.length;

        const result = await Swal.fire({
            title: 'Do you want to sync attributes?',
            html: `
            <div style="text-align: left; margin: 20px 0;">
                <p style="margin-bottom: 10px;"><strong>Selected Attributes: ${selectedCount}</strong></p>
                <p style="margin-bottom: 15px; ">This action will update the values of selected attributes in the child model's attributes.</p>
               
            </div>
        `,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#28a745',
            cancelButtonColor: '#dc3545',
            confirmButtonText: 'Yes, Sync',
            cancelButtonText: 'No, Cancel',
            reverseButtons: true,
            customClass: {
                popup: 'swal2-popup-custom'
            }
        });

        if (result.isConfirmed) {
            try {
                
                setIsSyncing(true);

                const response = await syncAttribute(
                    selectedAttributesData.masterModelId.toString(),
                    selectedAttributesData.selectedAttributes
                );

                if (response && response.status === 200) {
                    const actualCount = response.data?.data?.count

                    const syncData = response.data.data || {}
                    console.log("Sync data:", syncData);

                    Swal.fire({
                        icon: 'success',
                        title: 'Successfully Synced!',
                        text: `${actualCount} products have been successfully updated.`,
                        confirmButtonText: 'OK',
                        timer: 3000,
                        timerProgressBar: true
                    });

                    if (syncData && onSyncEdit) {
                        onSyncEdit(data)
                    }
                   
                } else {
                    console.error("Sync failed:", response);
                    Swal.fire({
                        icon: 'error',
                        title: 'Sync Failed',
                        text: 'There was an issue with syncing. Please try again.',
                        confirmButtonText: 'OK'
                    });
                }
            } catch (error) {
                console.error("Error syncing attributes:", error);
                Swal.fire({
                    icon: 'error',
                    title: 'Error!',
                    text: 'An error occurred while syncing attributes.',
                    confirmButtonText: 'OK'
                });
            } finally {
                setIsSyncing(false);
               
            }
        }
    };

    const handleAttributeCheckboxChange = (attributeIndex: number) => {
        const newSelectedAttributes = {
            ...selectedAttributes,
            [attributeIndex]: !selectedAttributes[attributeIndex]
        };

        setSelectedAttributes(newSelectedAttributes);

        // Log selected attributes with master model info in the required format
        const selectedAttributesData = getSelectedAttributesArray(newSelectedAttributes);
        console.log("Selected Attributes Updated:", selectedAttributesData);
    };

    const handleSelectAllAttributes = () => {
        if (masterModel.attributes && Array.isArray(masterModel.attributes)) {
            const allSelected = masterModel.attributes.every((_, index) => selectedAttributes[index]);
            const newSelected: { [key: number]: boolean } = {};
            masterModel.attributes.forEach((_, index) => {
                newSelected[index] = !allSelected;
            });
            setSelectedAttributes(newSelected);
        }
    };

    useEffect(() => {
        if (onFormDataChange) {
            // Get the selected attributes in the required format
            const selectedAttributesData = getSelectedAttributesArray(selectedAttributes);

            onFormDataChange({
                keyword,
                masterModel,
                selectedAttributes,
                selectedAttributesData // Pass the formatted data with master model info
            });
        }
    }, [keyword, masterModel, selectedAttributes]);

    const handleEditProduct = (productId: string | number) => {
        const url = process.env.REACT_APP_BASE_URL + `/product/edit?q=${productId}`;
        window.open(url, '_blank');
    };

    return (

        <div>
            {isSyncing && (
                <div className="overlay-loader">
                    <div className="loader-container">
                        <div className="spinner"></div>
                        <div className="loader-text">Searching products...</div>
                    </div>
                </div>
            )}

            <div className="card p-5 w-100" style={{ maxWidth: "100%", minWidth: "100%" }}>
                <div className="row mb-4 ">
                    {/* First Input: Enter Keyword */}
                    <div className="col-md-6">
                        <label className="form-label fs-6 fw-bolder text-dark required">
                            Enter Keyword
                        </label>
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Enter product keyword"
                            value={keyword}
                            onChange={handleKeywordChange}
                        />
                        <small className="text-muted">Enter a single keyword to identify this product</small>
                    </div>

                    {/* Second Input: Search Products To Select The Master Model */}
                    <div className="col-md-6">
                        <div className="d-flex align-items-center justify-content-start">
                            <label className="form-label fs-6 fw-bolder text-dark required">
                                Search Products To Select The Master Model
                            </label>
                            <MasterCustomTooltip
                                value="If a suitable master model does not exist in the Products list, create a master model in the Products module and add the attributes and values required. See Help module for more information."
                                placement="top"
                                className="ms-2 mb-2"
                                option=""
                            />
                        </div>
                        <div className="position-relative" ref={searchContainerRef}>
                            <div className="input-group">
                                <input
                                    value={productName}
                                    onChange={handleProductNameChange}
                                    onKeyPress={handleKeyPress}
                                    disabled={isLoading}
                                    className={clsx("form-control bg-transparent")}
                                    type="text"
                                    placeholder="Enter product name and press Enter to search"
                                    onFocus={() => {
                                        if (productSuggestions.length > 0) {
                                            setShowSuggestions(true);
                                        }
                                    }}
                                />
                                <button
                                    className="btn btn-primary"
                                    onClick={handleSearch}
                                    disabled={isLoading}
                                    type="button"
                                >
                                    {isLoading ? (
                                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                    ) : (
                                        "Search"
                                    )}
                                </button>
                            </div>

                            {/* Master Model Deleted Warning Message */}
                            {isMasterModelDeleted && (
                                <div className=" d-flex align-items-center">
                                    <i className="fas fa-exclamation-triangle me-2" style={{ color: "red" }}></i>
                                    <span style={{ fontWeight: "500"  , color: "red"}}>
                                        Master model is deleted. Please select a new master model.
                                    </span>
                                </div>
                            )}

                            {/* Suggestions Dropdown */}
                            {showSuggestions && productSuggestions.length > 0 && (
                                <div
                                    className="position-absolute w-100 bg-white border border-top-0 shadow-lg rounded-bottom-2 overflow-hidden"
                                    style={{ top: '100%', zIndex: 1050, maxHeight: '250px' }}
                                >
                                    <div className="overflow-auto" style={{ maxHeight: '200px' }}>
                                        {productSuggestions.map((suggestion, index) => (
                                            <div
                                                key={index}
                                                className="px-3 py-2 cursor-pointer border-bottom border-light hover-bg-light d-flex align-items-center"
                                                style={{ transition: 'background-color 0.15s ease-in-out', minHeight: '40px' }}
                                                onClick={() => handleSelectProduct(suggestion)}
                                                onMouseDown={(e) => e.preventDefault()}
                                            >
                                                <span className="text-truncate">
                                                    {suggestion.displayName || suggestion.productName || JSON.stringify(suggestion)}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                    {hasMoreResults && (
                                        <div className="border-top bg-light">
                                            <button
                                                className="btn btn-link text-primary w-100 py-2 text-decoration-none fw-medium"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    loadMoreResults();
                                                }}
                                                onMouseDown={(e) => e.preventDefault()}
                                                disabled={loadingMore}
                                                style={{ fontSize: '0.875rem' }}
                                            >
                                                {loadingMore ? (
                                                    <>
                                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                        Loading more results...
                                                    </>
                                                ) : (
                                                    <>
                                                        <i className="fas fa-chevron-down me-2"></i>
                                                        Show more results
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {masterModel && masterModel.attributes && Array.isArray(masterModel.attributes) && masterModel.attributes.length > 0 && masterModel.displayName ? (

                <div className="card mt-5 mb-5 w-100" style={{ marginTop: "80px", padding: "10px", position: "relative" }}>
                    <div className="card-header d-flex justify-content-between align-items-center">
                        <h6 className="card-title fs-5 mb-0">
                            Master Model Attributes
                        </h6>
                        {/* Only show buttons when showSyncButton is true */}
                        {showSyncButton && (
                            <div className="d-flex gap-2">
                                <button
                                    className="btn btn-sm btn-outline-primary"
                                    onClick={handleSelectAllAttributes}
                                    type="button"
                                >
                                    {masterModel.attributes && masterModel.attributes.every((_, index) => selectedAttributes[index]) ?
                                        'Deselect All' : 'Select All'}
                                </button>
                                <button
                                    className="btn btn-sm btn-success"
                                    onClick={handleSyncAttributes}
                                    disabled={isSyncing || Object.values(selectedAttributes).every(val => !val)}
                                    type="button"
                                >
                                    {isSyncing ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                            Syncing...
                                        </>
                                    ) : (
                                        <>
                                            <i className="fas fa-sync me-2"></i>
                                            Sync Selected Attributes
                                        </>
                                    )}
                                </button>
                            </div>
                        )}
                    </div>
                    <div className="card-body" style={{ padding: "10px" }}>
                        <div
                            className="table-responsive"
                            style={{
                                overflowX: "auto",
                                overflowY: "hidden",
                                width: "100%",
                                maxWidth: "100%",
                            }}
                        >
                            <table
                                className="table table-bordered mb-0"
                                style={{
                                    width: "100%",
                                    minWidth: `${300 + (masterModel.attributes.length * 220)}px`,
                                    tableLayout: "fixed",
                                    marginBottom: "0"
                                }}
                            >
                                <thead style={{ backgroundColor: "#f8f9fa" }}>
                                    <tr>
                                        <th
                                            style={{
                                                width: "300px",
                                                minWidth: "300px",
                                                maxWidth: "300px",
                                                fontWeight: "bold",
                                                padding: "12px",
                                                whiteSpace: "nowrap",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                position: "sticky",
                                                left: "0",
                                                backgroundColor: "#f8f9fa",
                                                zIndex: "10"
                                            }}
                                        >
                                            Product Name
                                        </th>
                                        {masterModel.attributes.map((attr: any, index: number) => (
                                            <th
                                                key={index}
                                                style={{
                                                    width: "220px",
                                                    minWidth: "220px",
                                                    maxWidth: "220px",
                                                    fontWeight: "bold",
                                                    padding: "12px",
                                                    whiteSpace: "nowrap",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis"
                                                }}
                                            >
                                                <div className="d-flex align-items-center gap-2">
                                                    <input
                                                        type="checkbox"
                                                        className="form-check-input"
                                                        checked={selectedAttributes[index] || false}
                                                        onChange={() => handleAttributeCheckboxChange(index)}
                                                        style={{ marginTop: "0", width: "18px", height: "18px" }}
                                                    />
                                                    <span className="text-truncate">
                                                        {attr.attributeName || `Attribute ${index + 1}`}
                                                    </span>
                                                </div>
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td
                                            style={{
                                                width: "300px",
                                                minWidth: "300px",
                                                maxWidth: "300px",
                                                fontWeight: "bold",
                                                padding: "12px",
                                                whiteSpace: "nowrap",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                position: "sticky",
                                                left: "0",
                                                backgroundColor: "#fff",
                                                zIndex: "10"
                                            }}
                                        >
                                            <i
                                                className="fas fa-edit text-primary mx-2"
                                                style={{ cursor: "pointer", fontSize: "14px" }}
                                                title="Edit Product"
                                                onClick={() => {
                                                    if (masterModel?.id !== null && masterModel?.id !== undefined) {
                                                        handleEditProduct(masterModel.id);
                                                    }
                                                }}
                                            ></i>
                                            {masterModel.displayName || "N/A"}
                                        </td>
                                        {masterModel.attributes.map((attr: any, index: number) => (
                                            <td
                                                key={index}
                                                style={{
                                                    width: "220px",
                                                    minWidth: "220px",
                                                    maxWidth: "220px",
                                                    fontWeight: "bold",
                                                    padding: "12px",
                                                    whiteSpace: "nowrap",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    backgroundColor: selectedAttributes[index] ? "#ffffffff" : "transparent"
                                                }}
                                            >
                                                {attr.value || "N/A"}
                                            </td>
                                        ))}
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="text-center mt-5 mb-5 text-muted">
                    {/* No master model data available */}
                </div>
            )}
        </div>
    );
};

export default MasterModelSelector;